import { createRouter, createWebHistory } from 'vue-router'

import store from '@/store';
import actions from '@/actions';
import { getWorkspaceId, hasOrganisationFeature, yabbrAuthRedirect } from '@/utils';
import { activeWorkspace, isPrincipal } from '@/selectors';

const routes = [
  {
    path: '/',
    name: 'Home',
    meta: {
      requires: ['sessionToken'],
    },
    redirect: { name: 'Poll List' },
  },
  {
    path: '/polls',
    name: 'Poll List',
    meta: {
      requires: ['sessionToken'],
    },
    component: () => import(/* webpackChunkName: "pollList" */ '@/components/PollList.vue'),
  },
  {
    path: '/polls/:pollId/',
    name: 'Poll Detail',
    meta: {
      requires: ['sessionToken'],
    },
    component: () => import(/* webpackChunkName: "pollDetail" */ '@/components/PollDetail/Overview.vue'),
    redirect: { name: 'Poll Setup' },
    children: [
      {
        name: 'Poll Setup',
        path: 'setup',
        meta: {
          requires: ['sessionToken', 'activeWorkspaceId'],
        },
        component: () => import(/* webpackChunkName: "pollDetail" */ '@/components/PollDetail/PollSetup.vue'),
      },
      {
        name: 'Poll Questions',
        path: 'questions',
        meta: {
          requires: ['sessionToken', 'activeWorkspaceId'],
        },
        component: () => import(/* webpackChunkName: "pollDetail" */ '@/components/PollDetail/PollQuestions.vue'),
      },
      {
        name: 'Poll Results',
        path: 'results',
        meta: {
          requires: ['sessionToken', 'activeWorkspaceId'],
        },
        component: () => import(/* webpackChunkName: "pollDetail" */ '@/components/PollDetail/PollResults.vue'),
      },
      {
        name: 'Poll Resources',
        path: 'resources',
        meta: {
          requires: ['sessionToken', 'activeWorkspaceId', 'principalAdmin', 'feature=polls.resources'],
        },
        component: () => import(/* webpackChunkName: "pollDetailAdmin" */ '@/components/PollDetail/PollResources.vue'),
      },
      {
        name: 'Poll Reporting',
        path: 'reporting',
        meta: {
          requires: ['sessionToken', 'activeWorkspaceId', 'principalAdmin', 'feature=polls.reporting'],
        },
        component: () => import(/* webpackChunkName: "pollDetailDetail" */ '@/components/PollDetail/PollReporting.vue'),
      },
    ],
  },
  {
    path: '/admin/overview',
    name: 'Admin Overview',
    meta: {
      requires: ['sessionToken', 'principalAdmin'],
    },
    component: () => import(/* webpackChunkName: "pollAdmin" */ '@/components/AdminOverview/AdminList.vue'),
  },
  {
    path: '/files/combiner',
    name: 'File Combiner',
    meta: {
      requires: ['sessionToken', 'principalAdmin'],
    },
    component: () => import(/* webpackChunkName: "fileManipulation" */ '@/components/FileManipulation/ReportCombiner.vue'),
  },
  {
    path: '/files/normalise',
    name: 'File Validation',
    meta: {
      requires: ['sessionToken', 'principalAdmin'],
    },
    component: () => import(/* webpackChunkName: "fileManipulation" */ '@/components/FileManipulation/FileNormalisation.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.query.action) {
    if (to.query.token) {
      localStorage.setItem('yabbr-token', to.query.token);
    }

    if (to.query.organisationId) {
      localStorage.setItem('yabbr-organisation', to.query.organisationId);
    }

    if (to.query.mfa) {
      localStorage.setItem('yabbr-mfa', to.query.mfa);
    }
    const { token, organisationId, mfa, action, ...rest } = to.query;
    router.replace({ path: to.path, query: rest });
  }

  const reqs = [
    ...new Set(
      to.matched.reduce((acc, cv) => (acc.concat(cv.meta.requires || [])), []),
    ),
  ];

  store.dispatch(actions.setCurrentRoute(to));

  if (!store.getState().user && localStorage.getItem('yabbr-token')) {
    store.dispatch(actions.addLoad());
    try {
      await store.dispatch(actions.appInit());
    } catch (e) {
      console.warn(e);
      store.dispatch(actions.endSession());
      yabbrAuthRedirect('App Init');
      return next(false);
    } finally {
      store.dispatch(actions.removeLoad());
    }
  }

  if (reqs.includes('sessionToken') && !localStorage.getItem('yabbr-token')) {
    yabbrAuthRedirect('Requires Session Token');
    return next(false);
  }

  if (reqs.includes('activeWorkspaceId') && !getWorkspaceId()) {
    // we dont have a workspace ID in storage
    // the token has been deleted, or they have no workspaces in Yabbr.
    // the user has to choose a workspace to use the app, so take them to yabbr to select one.
    yabbrAuthRedirect('Requires Active Workspace ID');
    return next(false);
  }

  // Not principal
  if (!isPrincipal(store.getState())) {
    const featureRequirements = reqs.filter((req) => req.startsWith('feature='));
    const missingFeatures = featureRequirements
      .some((req) => !hasOrganisationFeature(activeWorkspace(store.getState()), req.split('=')[1].split('.')));

    // Page requires principal and no feature flag overrides
    //  OR
    // Feature flag overrides exist, but some are missing
    if ((reqs.includes('principalAdmin') && !featureRequirements.length) || (featureRequirements.length && missingFeatures)) {
      store.dispatch(actions.setSnackbar({
        message: `You do not have permission to view ${to.name}`,
        timeout: 5000,
        color: 'error',
      }));

      return next(false);
    }
  }

  return next();
});

export default router;
